import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

export default function Header({ lang }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const base = `/${lang}`;

  const links = [
    { key: 'how', label: t('nav.how'), href: `${base}#how` },
    { key: 'projects', label: t('nav.projects'), href: `${base}#projects` },
    { key: 'transparency', label: t('nav.transparency'), href: `${base}#transparency` },
    { key: 'faq', label: t('nav.faq'), href: `${base}#faq` },
    { key: 'contact', label: t('nav.contact'), href: `${base}#contact` },
  ];

  const switchLang = (target) => {
    if (target === lang) return;
    const path = location.pathname.replace(/^\/(hr|en)/, `/${target}`);
    navigate(path + location.hash, { replace: false });
  };

  const scrollToHash = (e, href) => {
    const [pathname, hash] = href.split('#');
    if (!hash) return;
    if (location.pathname === pathname || location.pathname === `${pathname}/`) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <header className={`aq-header ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="aq-container aq-header__inner">
        <Link to={base} className="aq-header__brand" data-testid="header-brand">
          <Logo size={30} />
        </Link>

        <nav className="aq-header__nav" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              onClick={(e) => scrollToHash(e, l.href)}
              className="aq-header__link"
              data-testid={`nav-${l.key}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="aq-header__actions">
          <div className="aq-lang" role="group" aria-label={t('nav.switch_language')}>
            <button
              type="button"
              className={`aq-lang__btn ${lang === 'hr' ? 'is-active' : ''}`}
              onClick={() => switchLang('hr')}
              data-testid="lang-hr"
              aria-pressed={lang === 'hr'}
            >
              HR
            </button>
            <span className="aq-lang__sep" aria-hidden="true">/</span>
            <button
              type="button"
              className={`aq-lang__btn ${lang === 'en' ? 'is-active' : ''}`}
              onClick={() => switchLang('en')}
              data-testid="lang-en"
              aria-pressed={lang === 'en'}
            >
              EN
            </button>
          </div>

          <a
            href={`${base}#contact`}
            onClick={(e) => scrollToHash(e, `${base}#contact`)}
            className="aq-btn aq-btn--primary aq-header__cta"
            data-testid="header-cta"
          >
            {t('hero.cta_primary')}
          </a>

          <button
            type="button"
            className={`aq-hamburger ${open ? 'is-open' : ''}`}
            aria-expanded={open}
            aria-controls="aq-mobile-nav"
            aria-label={open ? t('nav.close_menu') : t('nav.open_menu')}
            onClick={() => setOpen((v) => !v)}
            data-testid="hamburger"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      <div id="aq-mobile-nav" className={`aq-mobile-nav ${open ? 'is-open' : ''}`}>
        <div className="aq-container">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              onClick={(e) => {
                setOpen(false);
                scrollToHash(e, l.href);
              }}
              className="aq-mobile-nav__link"
              data-testid={`mnav-${l.key}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={`${base}#contact`}
            onClick={(e) => {
              setOpen(false);
              scrollToHash(e, `${base}#contact`);
            }}
            className="aq-btn aq-btn--primary aq-mobile-nav__cta"
          >
            {t('hero.cta_primary')}
          </a>
        </div>
      </div>
    </header>
  );
}
